// Per-user POS billing layout preference. Persisted in localStorage keyed by
// userId so different cashiers on the same device see their own choice.
// Follow-up: move to a real per-user preferences endpoint when one exists.

import { useCallback, useEffect, useState } from 'react';

export type CartPanelPosition = 'LEFT' | 'RIGHT';

export const DEFAULT_CART_PANEL_POSITION: CartPanelPosition = 'RIGHT';

const KEY_PREFIX = 'bbpos_cart_panel_position:';

function storageKey(userId: string): string {
  return KEY_PREFIX + userId;
}

export function loadCartPanelPosition(userId: string | undefined | null): CartPanelPosition {
  if (!userId) return DEFAULT_CART_PANEL_POSITION;
  try {
    const val = localStorage.getItem(storageKey(userId));
    return val === 'LEFT' ? 'LEFT' : DEFAULT_CART_PANEL_POSITION;
  } catch {
    return DEFAULT_CART_PANEL_POSITION;
  }
}

export function saveCartPanelPosition(
  userId: string | undefined | null,
  position: CartPanelPosition,
): void {
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId), position);
  } catch {
    // ignore quota / privacy mode errors
  }
}

export function useCartPanelPosition(
  userId: string | undefined | null,
): [CartPanelPosition, (next: CartPanelPosition) => void] {
  const [position, setPosition] = useState<CartPanelPosition>(() => loadCartPanelPosition(userId));

  useEffect(() => {
    setPosition(loadCartPanelPosition(userId));
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const key = storageKey(userId);
    const handler = (e: StorageEvent) => {
      if (e.key === key) setPosition(loadCartPanelPosition(userId));
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [userId]);

  const update = useCallback(
    (next: CartPanelPosition) => {
      saveCartPanelPosition(userId, next);
      setPosition(next);
    },
    [userId],
  );

  return [position, update];
}
